// iter158 — ProductCard: gerçek marka logosu (pırlanta fallback yok), fiyat 0'sa gizli
import { Link } from "react-router-dom";
import { fmtTRY } from "../../lib/format";
import { useCart } from "../../context/CartContext";
import { ShoppingCart, Package, Store, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const track = (event, product_id) => { try { axios.post(`${BACKEND}/api/shop/analytics/track`, { event, product_id }); } catch {} };

export default function ProductCard({ p }) {
  const { add } = useCart();
  const price = p.price ?? p.unit_price ?? 0;
  const inStock = p.in_stock ?? ((p.stock_qty ?? p.quantity ?? 0) > 0);
  const brandLabel = p.brand_name || p.brand || "";
  const brandLogo = p.brand_logo || "";
  const skuLabel = p.sku || p.code || "";

  const features = (() => {
    const out = [];
    if (p.attributes) {
      const keys = Object.keys(p.attributes).slice(0, 3);
      keys.forEach(k => { if (p.attributes[k]) out.push(`${p.attributes[k]}`); });
    }
    if (out.length === 0) {
      if (p.tags) (Array.isArray(p.tags) ? p.tags : []).slice(0, 3).forEach(t => out.push(t));
    }
    return out.slice(0, 3);
  })();

  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    add({ ...p, unit_price: price }, 1);
    toast.success(`${p.name} sepete eklendi`);
    track("add_to_cart", p.id);
  };

  return (
    <Link
      to={`/urun/${p.sku || p.slug || p.id}`}
      onClick={() => track("click", p.id)}
      className="group block bg-white rounded-2xl border border-zinc-100 hover:border-zinc-200 hover:shadow-xl hover:shadow-zinc-900/5 transition-all duration-300 overflow-hidden"
      data-testid={`product-card-${p.id}`}
    >
      {/* Ürün görseli — temiz, üstünde badge yok */}
      <div className="aspect-[4/5] bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center relative overflow-hidden">
        {p.image ? (
          <img src={p.image} alt={p.name}
               loading="lazy" decoding="async" fetchpriority="low"
               width="400" height="500"
               className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"/>
        ) : (
          <Package size={56} className="text-zinc-300" />
        )}
        {/* Sadece Teşhirde etiketi — hafif */}
        {p.in_showroom && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur text-amber-700 text-[10px] font-semibold px-2 py-1 rounded-full shadow-sm" data-testid="badge-showroom">
              <Store size={10}/> Teşhirde
            </span>
          </div>
        )}
        {/* Hover'da sepete ekle */}
        <button
          onClick={handleAdd}
          className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-zinc-900 text-white hover:bg-primary shadow-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center"
          data-testid={`product-add-${p.id}`}
          title="Sepete Ekle"
          aria-label="Sepete Ekle"
        >
          <ShoppingCart size={17}/>
        </button>
      </div>

      {/* İçerik */}
      <div className="p-4">
        {/* Marka satırı — gerçek logo + isim */}
        {brandLabel && (
          <div className="flex items-center gap-2 mb-1" data-testid={`product-brand-${p.id}`}>
            {brandLogo && (
              <div className="w-6 h-6 rounded bg-white border border-zinc-100 flex items-center justify-center overflow-hidden shrink-0">
                <img src={brandLogo} alt={brandLabel} className="max-h-full max-w-full object-contain"/>
              </div>
            )}
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold truncate" title={brandLabel}>
              {brandLabel}
            </span>
          </div>
        )}
        <div className="font-medium text-zinc-900 text-[14px] mt-0.5 line-clamp-2 leading-snug min-h-[38px] tracking-tight">
          {p.name}
        </div>
        {features.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {features.map((f, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600 font-medium">{f}</span>
            ))}
          </div>
        )}
        {p.rating_count > 0 && (
          <div className="flex items-center gap-1 mt-2 text-[11px] text-amber-600">
            <Star size={11} className="fill-amber-500 text-amber-500"/>
            <strong className="tabular-nums">{p.rating_avg?.toFixed(1)}</strong>
            <span className="text-zinc-400">({p.rating_count})</span>
          </div>
        )}
        {/* Fiyat — sadece geçerli fiyat varsa göster */}
        {price > 0 ? (
          <div className="mt-3 pt-3 border-t border-zinc-100 flex items-end justify-between gap-2">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-400">Fiyat</div>
              <div className="text-[17px] font-bold text-zinc-900 tabular-nums leading-none mt-1" data-testid={`product-price-${p.id}`}>
                {fmtTRY(price)}
              </div>
            </div>
            {inStock && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                Stokta
              </span>
            )}
          </div>
        ) : (
          <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-2">
            <div className="text-[11px] text-zinc-400 font-medium">Fiyat için tıklayın</div>
            {inStock && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                Stokta
              </span>
            )}
          </div>
        )}
        {skuLabel && <div className="text-[9px] font-mono text-zinc-300 mt-2 tracking-tight">#{skuLabel}</div>}
      </div>
    </Link>
  );
}
